import { useMemo, useRef, useState, type JSX, type KeyboardEvent, type MouseEvent } from 'react';
import type { Id } from '../../../core/model';
import { Icon } from '../../components/Icon';
import { useActions, useEditedSceneId, useProject, useUi, useUiContext } from '../../context';
import { attempt } from '../../safe';
import { neighbour, selectWithModifiers, type DropPosition } from '../listMath';
import { addSourceMenu, itemContextMenu } from './itemMenu';
import { SourceRow } from './SourceRow';
import { flattenTree, indexTree, resolveDrop, treeSignature } from './tree';
import '../panels.css';

export function SourcesPanel(): JSX.Element {
  const { studio, ui } = useUiContext();
  const actions = useActions();
  const sceneId = useEditedSceneId();
  const selection = useUi((s) => s.selection);
  const collapsed = useUi((s) => s.collapsedGroups);
  // The signature changes only with the tree structure, so transform drags do not re-render the list.
  const signature = useProject((p) => treeSignature(sceneId ? p.scenes[sceneId]?.items : undefined));
  const [renaming, setRenaming] = useState<Id | null>(null);
  const [drop, setDrop] = useState<{ id: Id; position: DropPosition } | null>(null);
  const anchor = useRef<Id | null>(null);
  const list = useRef<HTMLUListElement>(null);

  const rows = useMemo(() => {
    const scene = sceneId ? studio.project.getState().project.scenes[sceneId] : undefined;
    return scene ? flattenTree(scene.items, collapsed) : [];
    // `signature` stands in for the scene's structure.
  }, [studio, sceneId, collapsed, signature]);
  const order = rows.map((r) => r.id);
  const selected = new Set(selection);
  const primary = selection[selection.length - 1] ?? null;

  const select = (id: Id, event: MouseEvent): void => {
    const next = selectWithModifiers(order, selection, id, { toggle: event.ctrlKey || event.metaKey, range: event.shiftKey }, anchor.current);
    if (!event.shiftKey) anchor.current = id;
    ui.getState().select(next);
  };

  const openMenu = (id: Id | null, x: number, y: number): void => {
    if (id && !selected.has(id)) ui.getState().select([id]);
    ui.getState().openMenu({ x, y, items: itemContextMenu(studio, ui, actions, id), returnFocus: list.current });
  };

  const onDropRow = (draggedId: Id, targetId: Id, position: DropPosition): void => {
    setDrop(null);
    const scene = sceneId ? studio.project.getState().project.scenes[sceneId] : undefined;
    if (!scene || !sceneId) return;
    const tree = indexTree(scene.items);
    // Dragging a selected row carries the rest of the selection, except items that travel inside a selected group.
    const carried = selected.has(draggedId)
      ? order.filter((id) => selected.has(id) && !tree.get(id)?.ancestors.some((ancestor) => selected.has(ancestor)))
      : [draggedId];
    if (carried.includes(targetId) || carried.some((id) => tree.get(targetId)?.ancestors.includes(id))) return;
    // 'before' stacks each row directly above the target, so the top-most goes first; the other positions fill from the bottom.
    const sequence = position === 'before' ? carried : [...carried].reverse();
    attempt(studio, () => {
      for (const id of sequence) {
        const current = studio.project.getState().project.scenes[sceneId];
        const target = current ? resolveDrop(indexTree(current.items), id, targetId, position) : null;
        if (target) {
          studio.commands.moveItem(sceneId, id, target.toIndex, target.groupId, (itemId) => studio.getItemContentSize(sceneId, itemId));
        }
      }
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    if (renaming) return;
    let handled = true;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const next = neighbour(order, primary, event.key === 'ArrowDown' ? 1 : -1);
      if (next) {
        anchor.current = next;
        ui.getState().select([next]);
      }
    } else if (event.key === 'F2' && primary) setRenaming(primary);
    else if (event.key === 'Enter' && primary) actions.openProperties(primary);
    else if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && primary) {
      const row = rows.find((r) => r.id === primary);
      if (row?.type === 'group' && row.collapsed === (event.key === 'ArrowRight')) ui.getState().toggleCollapsed(primary);
    } else if (event.key === 'Delete' || event.key === 'Backspace') actions.removeItems();
    else handled = false;
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const none = selection.length === 0;
  return (
    <div className="ui-panel">
      <div
        className="ui-panel-body"
        onContextMenu={(e) => {
          if (e.target === e.currentTarget || e.target === list.current) {
            e.preventDefault();
            openMenu(null, e.clientX, e.clientY);
          }
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget || e.target === list.current) ui.getState().select([]);
        }}
      >
        {rows.length === 0 ? <div className="ui-empty">No sources in this scene. Use + to add one.</div> : null}
        <ul
          ref={list}
          className="ui-list"
          role="tree"
          aria-label="Sources"
          aria-multiselectable="true"
          aria-activedescendant={primary ? `item-row-${primary}` : undefined}
          tabIndex={0}
          onKeyDown={onKeyDown}
        >
          {sceneId
            ? rows.map((row) => (
                <SourceRow
                  key={row.id}
                  sceneId={sceneId}
                  row={row}
                  selected={selected.has(row.id)}
                  renaming={renaming === row.id}
                  drop={drop?.id === row.id ? drop.position : null}
                  onRenameDone={() => {
                    setRenaming(null);
                    list.current?.focus();
                  }}
                  onSelect={select}
                  onOpen={(id) => {
                    const item = actions.findItem(id);
                    if (item?.type === 'source') actions.openProperties(id);
                    else setRenaming(id);
                  }}
                  onMenu={openMenu}
                  onDragOverRow={(id, position) => setDrop(position ? { id, position } : null)}
                  onDropRow={onDropRow}
                />
              ))
            : null}
        </ul>
      </div>
      <div className="ui-toolbar" role="toolbar" aria-label="Source actions">
        <button
          type="button"
          className="ui-icon-btn"
          aria-label="Add source"
          aria-haspopup="menu"
          title="Add source"
          disabled={!sceneId}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            ui.getState().openMenu({ x: rect.left, y: rect.top - 4, items: addSourceMenu(studio, ui, actions), returnFocus: e.currentTarget });
          }}
        >
          <Icon name="plus" />
        </button>
        <button type="button" className="ui-icon-btn" aria-label="Remove selected items" title="Remove" disabled={none} onClick={() => actions.removeItems()}>
          <Icon name="minus" />
        </button>
        <button type="button" className="ui-icon-btn" aria-label="Properties" title="Properties" disabled={selection.length !== 1} onClick={() => actions.openProperties()}>
          <Icon name="gear" />
        </button>
        <button type="button" className="ui-icon-btn" aria-label="Filters" title="Filters" disabled={selection.length !== 1} onClick={() => actions.openFilters()}>
          <Icon name="sliders" />
        </button>
        <span className="ui-grow" />
        <button type="button" className="ui-icon-btn" aria-label="Move item up" title="Move up" disabled={none} onClick={() => actions.order('up')}>
          <Icon name="up" />
        </button>
        <button type="button" className="ui-icon-btn" aria-label="Move item down" title="Move down" disabled={none} onClick={() => actions.order('down')}>
          <Icon name="down" />
        </button>
      </div>
    </div>
  );
}
